import { Color } from '@tmh-website/shared/colors';
import type { CanBeFullWidth, PrefixProperties } from '@tmh-website/shared/types';
import darken from 'polished/lib/color/darken';
import lighten from 'polished/lib/color/lighten';
import transparentize from 'polished/lib/color/transparentize';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import { ThemeType } from './theme';

export const resetButtonStylingCSS = css`
  appearance: none;
  padding: 0;
  font-family: inherit;
  color: inherit;
  cursor: pointer;
  background: transparent;
  border: none;
`;

interface GetSwiperPaginationStylesProps {
  textColor: Color;
  checkedColor: Color;
  backgroundColor: Color;
}

export function getSwiperPaginationStyles({
  textColor,
  checkedColor,
  backgroundColor,
}: GetSwiperPaginationStylesProps) {
  return css`
    /* Pagination base */
    .swiper-pagination-bullet {
      display: inline-block;
      width: 16px;
      height: 16px;
      margin: 0 3px;
      cursor: pointer;
      border: 2px solid ${props => transparentize(0.8, props.theme.color[textColor])};
      border-radius: ${({ theme }) => (theme.isBorderRadiusDisabled ? 0 : '99999px')};
      transition: background-color ${props => props.theme.transitionTiming},
        border-color ${props => props.theme.transitionTiming};

      &:hover:not(.swiper-pagination-bullet-active) {
        border-color: ${props => transparentize(0.9, props.theme.color[textColor])};
      }
    }

    .swiper-pagination-bullet-active {
      background-color: ${props => props.theme.color[checkedColor]};
      border-color: transparent;
    }

    /* Dynamic bullets */
    &.swiper-pagination-bullets-dynamic {
      display: flex;
      width: 154px !important;
      overflow: hidden;

      &::before,
      &::after {
        z-index: 2;
        display: block;
        flex-shrink: 0;
        width: 30px;
        height: 16px;
        pointer-events: none;
        content: '';
      }

      &::before {
        position: relative;
        margin-right: -8px;
        background: linear-gradient(
          to right,
          ${props => props.theme.color[backgroundColor]} 20%,
          ${props => transparentize(1, props.theme.color[backgroundColor])} 100%
        );
      }

      &::after {
        position: absolute;
        top: 0;
        right: 0;
        background: linear-gradient(
          to left,
          ${props => props.theme.color[backgroundColor]} 20%,
          ${props => transparentize(1, props.theme.color[backgroundColor])} 100%
        );
      }

      .swiper-pagination-bullet {
        position: relative;
        top: 0;
        flex-shrink: 0;
        transition: left ${props => props.theme.transitionTiming};
      }
    }
  `;
}

export interface SectionBaseProps {
  /**
   * Determines whether the section should have a bottom padding applied.
   */
  hasPaddingBottom?: boolean;

  /**
   * Determines whether the section should have a top padding applied.
   */
  hasPaddingTop?: boolean;

  smallPaddingTop?: boolean;
}

export type StyledSectionBaseProps = PrefixProperties<SectionBaseProps, '$'>;

export const SectionBase = styled.section<StyledSectionBaseProps>`
  // Prevent the vertical margins of the child elements from overflowing the <section> element.
  // @see https://stackoverflow.com/a/13573739/11869579
  overflow-y: auto;

  // Default paddings.
  padding: ${props => props.theme.sectionSpacing.mobile.medium} 0;
  @media (${props => props.theme.breakpoint.md.up}) {
    padding: ${props => props.theme.sectionSpacing.desktop.medium} 0;
  }

  // Some sections require a smaller padding from top.
  // @see https://www.figma.com/file/3igqZCzevXJm16vkmDCoZJ/TMH-Design-System-Web?type=design&node-id=995-33799&mode=design&t=TK3rOzBXYbMn5lRn-4
  ${props =>
    props.$hasPaddingTop &&
    props.$smallPaddingTop &&
    css`
      @media (${props => props.theme.breakpoint.md.up}) {
        padding-top: ${props => props.theme.sectionSpacing.desktop.small} !important;
      }
    `}

  // Above everything else, the Content Editor is able to completely disable
  // the top/bottom paddings for a given section.
  ${props =>
    props.$hasPaddingTop === false &&
    css`
      padding-top: 0 !important;
    `}
  ${props =>
    props.$hasPaddingBottom === false &&
    css`
      padding-bottom: 0 !important;
    `}
`;

/**
 * A helper function meant to simplify AND UNIFY the way how the props are being passed
 * to the `<SectionBase>` component.
 *
 * @param componentData - The component's data (probably fetched from the Strapi CMS).
 * @param [$smallPaddingTop] - Optional value for the `$smallPaddingTop` prop.
 *
 * @returns - An object compliant with the `StyledSectionBaseProps` interface, which can be later used
 *   e.g. with the spread operator.
 *
 * @example - usage:
 * <Styled.Section {...useSectionBase(data)}>...</Styled.Section>
 * <Styled.Section {...useSectionBase(data, true)}>...</Styled.Section>
 * <Styled.Section {...useSectionBase(data, smallPaddingTop)}>...</Styled.Section>
 *
 * @see SectionBase
 */
export const getStyledSectionBaseProps = <
  ComponentData extends Required<Pick<SectionBaseProps, 'hasPaddingBottom' | 'hasPaddingTop'>>
>(
  componentData: ComponentData,
  $smallPaddingTop?: StyledSectionBaseProps['$smallPaddingTop']
): StyledSectionBaseProps => ({
  $hasPaddingBottom: componentData.hasPaddingBottom,
  $hasPaddingTop: componentData.hasPaddingTop,
  $smallPaddingTop,
});

const GlobalAppStyles = createGlobalStyle<{ theme?: ThemeType }>`${css`
  html {
    scroll-behavior: smooth;
    text-size-adjust: 100%;

    &.is-route-change {
      scroll-behavior: auto !important;
    }
  }

  body {
    padding: 0;
    margin: 0;
    overflow-x: hidden;
    background-color: ${props => props.theme.color[Color.Neutral_0_White]};

    &.is-clipped {
      overflow: hidden;
    }
  }

  * {
    box-sizing: border-box;
  }

  // Disable text color on iOS devices
  input:is([type='button'], [type='submit'], [type='reset']),
  input[type='file']::file-selector-button,
  button {
    color: inherit;
  }

  .is-desktop-only {
    @media (${props => props.theme.breakpoint.md.down}) {
      display: none !important;
    }
  }

  .is-mobile-only {
    @media (${props => props.theme.breakpoint.md.up}) {
      display: none !important;
    }
  }
`}
`;

export type WrapperProps = PrefixProperties<
  {
    /**
     * Determines whether the wrapper should have no padding (on all sides).
     *
     * @default false
     */
    noPadding?: boolean;
  } & CanBeFullWidth,
  '$'
>;

const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  max-width: ${({ $isFullWidth, theme }) => ($isFullWidth ? null : theme.siteMaxWidth)};
  padding: 0 ${props => props.theme.wrapperPaddingMobile};
  margin: 0 auto;
  @media (${props => props.theme.breakpoint.md.up}) {
    padding: 0 ${props => props.theme.wrapperPadding};
  }

  ${props => props.$noPadding && 'padding: 0 !important;'};
`;

const WrapperSmall = styled.div`
  max-width: 640px;
  padding: 0 ${props => props.theme.wrapperPaddingMobile};
  margin: 0 auto;
  @media (${props => props.theme.breakpoint.md.up}) {
    padding: 0 ${props => props.theme.wrapperPadding};
  }

  section {
    margin: 40px 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-top: 0;
    }
  }
`;

const WrapperLarge = styled(Wrapper)`
  max-width: ${props => props.theme.siteMaxWidthLarge};
`;

/**
 * Skeleton styles
 */
const backgroundSize = '600px';

const loadingAnimation = keyframes`
  0% { background-position-x: 0 };
  100% { background-position-x: ${backgroundSize} };
`;

const skeletonLoadingCss = (color: Color, blendMode: 'lighten' | 'darken' = 'darken') => css`
  background: linear-gradient(
    to right,
    ${props =>
      `${props.theme.color[color]}  0%, ${
        blendMode === 'darken' ? darken(0.075, props.theme.color[color]) : lighten(0.075, props.theme.color[color])
      }  50%, ${props.theme.color[color]}  100%`}
  );
  background-position-x: 50%;
  background-size: ${backgroundSize};
  animation: ${loadingAnimation} 1.5s linear infinite;
`;

const srOnlyCss = css`
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(1px, 1px, 1px, 1px) !important;
  clip-path: inset(50%) !important;
  white-space: nowrap !important;
  border: 0 !important;
`;

// Group all exports to force naming conventions for imports
const StyledGlobal = {
  GlobalAppStyles,
  skeletonLoadingCss,
  srOnlyCss,
  Wrapper,
  WrapperLarge,
  WrapperSmall,
};

export default StyledGlobal;
